import axios from 'axios';
import type { AxiosResponse } from 'axios';
import type { CamelCasedPropertiesDeep } from 'type-fest';

import type { Site as SiteDto, SiteId } from './dto';

import type { FeedbackToolsCount, SendTrackingCodeByEmailRequestBody } from './model';
import { mapFeedbackToolsCountDtoToModel } from './mapper';

const ACCOUNTS_API_ROOT = '/api/v1/accounts/';

interface DeleteSiteResponse {
  sites: CamelCasedPropertiesDeep<SiteDto>[];
  success: boolean;
  error: Error;
}

interface RequestPlanUpdateOptions {
  accountId: number;
  siteId: number;
  planId: string;
  requestedFrom: string;
}

interface RequestPlanUpdateRequest {
  action: 'request_plan_update';
  // eslint-disable-next-line camelcase
  plan_id: string;
  // eslint-disable-next-line camelcase
  ui_location_requested_from: string;
}

interface RequestPlanUpdateResponse {
  success?: boolean;
  // eslint-disable-next-line camelcase
  error_message?: string;
}

interface SendTrackingCodeByEmailResponse {
  success: boolean;
  errorMessage?: string;
}

interface CreateSiteModel {
  success: boolean;
  siteId: number;
}

interface CreateSiteResponse {
  success: boolean;
  // eslint-disable-next-line camelcase
  site_id: number;
}

const getApiUrl = (accountId: number, siteId: number) =>
  `${ACCOUNTS_API_ROOT}${accountId}/sites/${siteId}`;

export const createSite = (
  accountId: number,
  orgId: number,
  siteData: { url: string; name?: string; industry: string },
): Promise<CreateSiteModel> =>
  axios
    .post<CreateSiteResponse>(`/api/v1/accounts/${accountId}/organizations/${orgId}/sites`, {
      action: 'add_site',
      ...siteData,
    })
    .then((response) => ({
      success: response.data.success,
      siteId: response.data.site_id,
    }));

export const activateTrial = (accountId: number, siteId: number): Promise<AxiosResponse> =>
  axios.post(`/api/v1/accounts/${accountId}/sites/${siteId}`, {
    action: 'activate_trial',
  });

export const deleteSite = (accountId: number, siteId: number): Promise<DeleteSiteResponse> =>
  axios.delete(getApiUrl(accountId, siteId)).then(({ data }) => {
    if (!data.success) {
      throw data.error;
    }
    return data;
  });

export const requestPlanUpdate = async ({
  accountId,
  siteId,
  planId,
  requestedFrom,
}: RequestPlanUpdateOptions): Promise<RequestPlanUpdateResponse> => {
  const requestData: RequestPlanUpdateRequest = {
    action: 'request_plan_update',
    plan_id: planId,
    ui_location_requested_from: requestedFrom,
  };

  const { data } = await axios.post<RequestPlanUpdateResponse>(
    getApiUrl(accountId, siteId),
    requestData,
  );

  return data;
};

export const sendTrackingCodeByEmail = async (
  accountId: number,
  siteId: number,
  body: SendTrackingCodeByEmailRequestBody,
): Promise<SendTrackingCodeByEmailResponse> => {
  const url = `${getApiUrl(accountId, siteId)}/share_tracking_code`;
  const { data } = await axios.post(url, {
    recipient_email: body.recipientEmail,
    locale: body.locale,
  });

  return { success: !!data.success, errorMessage: data.error_message };
};

interface UpdateSiteData {
  name: string;
  url: string;
}

interface UpdateSiteReponse {
  success: boolean;
  error: Error;
}

export const updateSite = async (
  site: Pick<SiteDto, 'id'> & { account: Pick<SiteDto['account'], 'id'> },
  { name, url }: UpdateSiteData,
): Promise<UpdateSiteReponse> => {
  if (!site.account.id) {
    return Promise.reject();
  }

  const { data } = await axios.put(getApiUrl(site.account.id, site.id), { name, url });

  if (!data.success) {
    throw data.error;
  }

  return data;
};

export const requestFeedbackToolsCount = async (
  siteId: SiteDto['id'],
): Promise<FeedbackToolsCount> => {
  if (!siteId) {
    return Promise.reject();
  }
  const { data } = await axios.get(`/api/v1/sites/${siteId}/feedback-tools`);

  return mapFeedbackToolsCountDtoToModel(data);
};

export const enableEnforceMfa = (siteId: number): Promise<AxiosResponse> => {
  return axios.post(`/api/v2/site/${siteId}/enforcemfa`);
};

export const disableEnforceMfa = (siteId: number): Promise<AxiosResponse> => {
  return axios.post(`/api/v2/site/${siteId}/unenforcemfa`);
};

// daily sessions API

// NOTE: we always want to use secure API, even when the player is loaded over HTTP. Axios automatically sets the
// protocol to https for us.
const SITES_API_ROOT = `/api/v1/sites`;

const createDailySessionsEndpointPath = (siteId: SiteId): string => {
  return `${SITES_API_ROOT}/${siteId}/daily-sessions`;
};

export interface DailySessionsData {
  sessions: number;
  days: number;
}

export const getDailySessions = async (siteId: SiteId): Promise<DailySessionsData> => {
  const path = createDailySessionsEndpointPath(siteId);
  const response = await axios.get<DailySessionsData>(path);

  return response.data;
};

// identified users custom events API

const createIUCEEndpointPath = (siteId: SiteId): string => {
  return `${SITES_API_ROOT}/${siteId}/identified-users-custom-events`;
};

interface IdentifiedUserCustomEventsDto {
  // eslint-disable-next-line camelcase
  identified_sessions: number | null; // Can be null when click house DB is not reacheable
  // eslint-disable-next-line camelcase
  custom_events: number | null; // Can be null when click house DB is not reacheable
  days: number;
}

export interface IdentifiedUserCustomEvents {
  identifiedSessions: number;
  customEvents: number;
  days: number;
}

export const getIdentifiedUserCustomEvents = async (
  siteId: SiteId,
): Promise<IdentifiedUserCustomEvents> => {
  const path = createIUCEEndpointPath(siteId);
  const response = await axios.get<IdentifiedUserCustomEventsDto>(path);

  return {
    identifiedSessions: response.data.identified_sessions ?? 0,
    customEvents: response.data.custom_events ?? 0,
    days: response.data.days,
  };
};
